'use client';

import { useEffect, useState, useMemo } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import styles from './AuditProgressNotification.module.css';

// ============================================
// Types
// ============================================

export interface AuditStep {
  id: string;
  label: string;
  labelEs?: string;
  duration: number; // estimated seconds
}

export interface AuditProgressNotificationProps {
  /** Whether the notification is visible */
  isVisible: boolean;
  /** URL being audited */
  url?: string;
  /** Audit tier for timing */
  tier?: 'free' | 'pro';
  /** Whether the audit is complete */
  isComplete?: boolean;
  /** Error message if failed */
  error?: string;
  /** Locale for translations */
  locale?: string;
  /** Callback when cancel is clicked */
  onCancel?: () => void;
}

// ============================================
// Constants
// ============================================

const STEPS_FREE: AuditStep[] = [
  { id: 'capture', label: 'Capturing screenshots', labelEs: 'Capturando pantallas', duration: 15 },
  { id: 'accessibility', label: 'Running accessibility checks', labelEs: 'Verificando accesibilidad', duration: 8 },
  { id: 'heuristic', label: 'Analyzing UX patterns', labelEs: 'Analizando patrones UX', duration: 10 },
  { id: 'report', label: 'Generating report', labelEs: 'Generando reporte', duration: 5 },
];

const STEPS_PRO: AuditStep[] = [
  { id: 'capture', label: 'Capturing multi-device screenshots', labelEs: 'Capturando pantallas multi-dispositivo', duration: 25 },
  { id: 'accessibility', label: 'Running deep accessibility audit', labelEs: 'Auditoria de accesibilidad profunda', duration: 10 },
  { id: 'analysis', label: 'Expert UX pattern analysis', labelEs: 'Analisis experto de patrones UX', duration: 30 },
  { id: 'heuristic', label: 'Applying 167 Audit Criteria', labelEs: 'Aplicando 167 Criterios de Auditoría', duration: 15 },
  { id: 'report', label: 'Generating comprehensive report', labelEs: 'Generando reporte completo', duration: 10 },
];

// ============================================
// Component
// ============================================

export function AuditProgressNotification({
  isVisible,
  url,
  tier = 'free',
  isComplete = false,
  error,
  locale = 'en',
  onCancel,
}: AuditProgressNotificationProps) {
  const steps = tier === 'pro' ? STEPS_PRO : STEPS_FREE;
  const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);

  // State
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Reset when visibility changes
  useEffect(() => {
    if (isVisible) {
      setElapsedTime(0);
      setActiveStepIndex(0);
    }
  }, [isVisible]);

  // Timer
  useEffect(() => {
    if (!isVisible || isComplete || error) return;

    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, isComplete, error]);

  // Progress through steps
  useEffect(() => {
    if (isComplete) {
      setActiveStepIndex(steps.length);
      return;
    }

    let accumulated = 0;
    for (let i = 0; i < steps.length; i++) {
      accumulated += steps[i].duration;
      if (elapsedTime < accumulated) {
        setActiveStepIndex(i);
        return;
      }
    }
    // Stay on last step if time exceeds
    setActiveStepIndex(steps.length - 1);
  }, [elapsedTime, steps, isComplete]);

  // Calculate progress percentage
  const progress = useMemo(() => {
    if (isComplete) return 100;
    if (error) return 0;

    // Calculate based on elapsed time
    const rawProgress = (elapsedTime / totalDuration) * 100;
    // Cap at 95% until complete
    return Math.min(95, Math.round(rawProgress));
  }, [elapsedTime, totalDuration, isComplete, error]);

  // Current step label
  const currentStepLabel = useMemo(() => {
    if (isComplete) {
      return locale === 'es' ? 'Analisis completo' : 'Analysis complete';
    }
    if (error) {
      return locale === 'es' ? 'Error en el analisis' : 'Analysis failed';
    }
    const step = steps[activeStepIndex];
    if (!step) return locale === 'es' ? 'Finalizando...' : 'Finalizing...';
    return locale === 'es' ? (step.labelEs || step.label) : step.label;
  }, [activeStepIndex, steps, isComplete, error, locale]);

  // i18n
  const i18n = {
    en: {
      analyzing: 'Analyzing',
      cancel: 'Cancel',
      validUrl: 'Valid URL',
    },
    es: {
      analyzing: 'Analizando',
      cancel: 'Cancelar',
      validUrl: 'URL valida',
    },
  };
  const t = i18n[locale as 'en' | 'es'] || i18n.en;

  if (!isVisible) return null;

  return (
    <div className={styles.container} role="status" aria-live="polite">
      {/* URL Display */}
      {url && (
        <div className={styles.urlRow}>
          <span className={styles.urlText}>{url}</span>
          <span className={styles.validBadge}>
            <CheckCircle size={12} />
            {t.validUrl}
          </span>
        </div>
      )}

      {/* Progress Section */}
      <div className={`${styles.progressSection} ${error ? styles.errorState : ''}`}>
        <div className={styles.progressHeader}>
          <div className={styles.statusRow}>
            {!error && !isComplete && (
              <Loader2 size={14} className={styles.spinner} />
            )}
            {isComplete && (
              <CheckCircle size={14} className={styles.checkIcon} />
            )}
            <span className={styles.statusText}>{currentStepLabel}...</span>
          </div>
          <span className={styles.percentage}>
            {new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-US').format(progress)}%
          </span>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressBarContainer}>
          <div
            className={`${styles.progressBar} ${isComplete ? styles.complete : ''} ${error ? styles.error : ''}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Cancel Button */}
      {onCancel && !isComplete && (
        <button className={styles.cancelButton} onClick={onCancel} type="button">
          {t.cancel}
        </button>
      )}
    </div>
  );
}

export default AuditProgressNotification;
